import {
  SimpleRoleType,
  WorkspaceSimpleRoleType,
} from "src/clients/taktile-api";
import { useRoleOverride } from "src/hooks/useCapabilities/RoleOverrideStore";
import { Capabilities } from "src/hooks/useCapabilities/types";
import { useIsTaktileOrgUser } from "src/hooks/useIsTaktileOrgUser";
import { useIsFWDOrAdmin } from "src/store/AuthStore";
import { useSimpleRole } from "src/store/SimpleRoleProvider";
import { assertUnreachable } from "src/utils/typeUtils";

const isRoleAtLeast = (
  requiredRole: SimpleRoleType,
  userRole: Nullable<SimpleRoleType>,
): boolean => {
  if (!userRole) {
    return false;
  }

  switch (requiredRole) {
    case SimpleRoleType.ADMIN:
      return [SimpleRoleType.ADMIN].includes(userRole);

    case SimpleRoleType.MEMBER:
      return [SimpleRoleType.ADMIN, SimpleRoleType.MEMBER].includes(userRole);

    default:
      assertUnreachable(requiredRole);
      return false;
  }
};

const hasAtLeastRequiredRole = (
  requiredRole: WorkspaceSimpleRoleType,
  userRole: Nullable<WorkspaceSimpleRoleType>,
): boolean => {
  if (!userRole) {
    return false;
  }
  switch (requiredRole) {
    case WorkspaceSimpleRoleType.ADMIN:
      return [WorkspaceSimpleRoleType.ADMIN].includes(userRole);

    case WorkspaceSimpleRoleType.EDITOR:
      return [
        WorkspaceSimpleRoleType.ADMIN,
        WorkspaceSimpleRoleType.EDITOR,
      ].includes(userRole);

    case WorkspaceSimpleRoleType.REVIEWER:
      return [
        WorkspaceSimpleRoleType.REVIEWER,
        WorkspaceSimpleRoleType.EDITOR,
        WorkspaceSimpleRoleType.ADMIN,
      ].includes(userRole);

    case WorkspaceSimpleRoleType.VIEWER:
      return [
        WorkspaceSimpleRoleType.ADMIN,
        WorkspaceSimpleRoleType.EDITOR,
        WorkspaceSimpleRoleType.VIEWER,
      ].includes(userRole);

    default:
      assertUnreachable(requiredRole);
      return false;
  }
};

export const isWorkspaceRoleAtLeast = (
  requiredRole: WorkspaceSimpleRoleType | WorkspaceSimpleRoleType[],
  userRole: Nullable<WorkspaceSimpleRoleType>,
): boolean => {
  const requiredRoles = Array.isArray(requiredRole)
    ? requiredRole
    : [requiredRole];

  return requiredRoles.some((role) => hasAtLeastRequiredRole(role, userRole));
};

export const useCapabilities = (): Capabilities => {
  const {
    role,
    workspaceRole: actualWorkspaceRole,
    isLoading,
  } = useSimpleRole();
  const actualIsFWDOrAdmin = useIsFWDOrAdmin();
  const isTaktileUser = useIsTaktileOrgUser();
  const { overrideWorkspaceRole, overrideFWDOrAdmin } = useRoleOverride();

  // Use overridden role if user is Taktile admin and override is set
  const workspaceRole =
    isTaktileUser && overrideWorkspaceRole !== null
      ? overrideWorkspaceRole
      : actualWorkspaceRole;
  const isFWDOrAdmin =
    isTaktileUser && overrideWorkspaceRole !== null
      ? overrideFWDOrAdmin
      : actualIsFWDOrAdmin;

  return {
    role,
    workspaceRole: workspaceRole,
    isLoading,
    flowVersions: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.VIEWER,
        workspaceRole,
      ),
      canPublish: (flow) => {
        const isReviewRequired = !!flow.review_configuration?.requires_review;
        return isWorkspaceRoleAtLeast(
          isReviewRequired
            ? WorkspaceSimpleRoleType.EDITOR
            : WorkspaceSimpleRoleType.ADMIN,
          workspaceRole,
        );
      },
      canArchive: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canSetSandboxDefault: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canSetDefault: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canCreate: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canEdit: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canDeleteDraft: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
    },
    datasets: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canDelete: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
    },
    decisionHistory: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
    },
    flows: {
      canAccess: isWorkspaceRoleAtLeast(
        [WorkspaceSimpleRoleType.VIEWER, WorkspaceSimpleRoleType.REVIEWER],
        workspaceRole,
      ),
      canCreate: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canEdit: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canDeleteUnpublished: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canDeletePublished: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
    },
    apiKeys: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
    },
    connections: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
    },
    usersPermissions: {
      canAccess: isRoleAtLeast(SimpleRoleType.ADMIN, role),
    },
    workspaces: {
      canCreate: isRoleAtLeast(SimpleRoleType.ADMIN, role),
      canEdit: isRoleAtLeast(SimpleRoleType.ADMIN, role),
    },
    outgoingWebhooks: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
    },
    manualReview: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.REVIEWER,
        workspaceRole,
      ),
    },
    flowCharts: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canCreate: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canEdit: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canDelete: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
    },
    jobs: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canCreate: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canEdit: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canDelete: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canDeleteSource: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canDeleteDestination: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canToggleStatus: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canRunSandbox: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canRunLive: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canChangeSchedule: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canDuplicate: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
    },
    flowFolders: {
      canCreate: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canEdit: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canDelete: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
    },
    auditLogs: {
      canAccess: isRoleAtLeast(SimpleRoleType.ADMIN, role),
    },
    outcomes: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canCreate: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canEdit: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canDelete: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canClear: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
    },
    featureCatalogue: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.VIEWER,
        workspaceRole,
      ),
      canCreate: isFWDOrAdmin,
      canEdit: isFWDOrAdmin,
      canDuplicate: isFWDOrAdmin,
    },
    featureQueriesCatalogue: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.VIEWER,
        workspaceRole,
      ),
      canCreate: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canEdit: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canDuplicate: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canActivate: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canBackfill: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canViewQueryDetails: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.VIEWER,
        workspaceRole,
      ),
      canCreateQuery: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canEditSandboxQuery: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canEditLiveQuery: isFWDOrAdmin,
      canPreviewLive: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canPreviewSandbox: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canChangeSandboxStatus: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canChangeLiveStatus: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canDeleteDisabledQuery: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canArchive: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
    },
    organization: {
      canEditLogo: isRoleAtLeast(SimpleRoleType.ADMIN, role),
    },
    sensitiveFields: {
      canReveal: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canEdit: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
    },
    apiDocs: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
    },
    caseTemplates: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canEdit: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
      canCreateVersion: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canCreate: isFWDOrAdmin,
      canArchivePublished: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
    },
    caseQueues: {
      canEdit: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canCreate: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
    },
    cases: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.REVIEWER,
        workspaceRole,
      ),
      canManage: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
      canAssign: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
    },
    caseFiles: {
      canEdit: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.REVIEWER,
        workspaceRole,
      ),
    },
    simulations: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
    },
    notebooks: {
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
    },
    caseHistory: {
      canSeeDetails: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.ADMIN,
        workspaceRole,
      ),
    },
    agents: {
      // Revisit after AGNT-88
      canAccess: isWorkspaceRoleAtLeast(
        WorkspaceSimpleRoleType.EDITOR,
        workspaceRole,
      ),
    },
    scimToken: {
      canAccess: isRoleAtLeast(SimpleRoleType.ADMIN, role),
    },
  };
};
